import { ComputedRef } from "vue";
import { BasePriceOverride, useBasePriceStore } from "@/modules/base_price/store";
import { DayMap, replaceMonth } from "@/modules/base_price/utils/dayDistribution";
import BasePriceOverrideType = Modules.BasePrice.Data.Overrides.BasePriceOverrideType;

/** The day-level rows that inherit month by month, by the field their days live in. */
const DAY_VALUES = {
	day_season: "daily_shares",
	day_fill: "daily_fill_rates",
	day_manual_cpm: "daily_cpm",
} as const;

export type MonthDayRowType = keyof typeof DAY_VALUES;

const valuesOf = (
	row: BasePriceOverride | null,
	type: MonthDayRowType
): DayMap<number> => (row?.[DAY_VALUES[type]] as DayMap<number> | undefined) ?? {};

/**
 * Reading and writing one month of a target's day-level rows, for the month
 * calendars.
 *
 * Locked days, day fill and day CPM all inherit per month: a row lists the
 * months it decides (`months`), and every other month follows the nearest
 * ancestor that decides it. So a calendar only ever touches its own month —
 * applying December makes December the target's own and nothing else, and
 * resetting December hands back December only.
 *
 * Pinned days (`day_budget`) never inherit and list no months; they get their
 * own two helpers here because the budget calendar writes them alongside its
 * locked days.
 */
export const useMonthDayRows = (
	monthKey: ComputedRef<string>,
	dates: ComputedRef<string[]>
) => {
	const store = useBasePriceStore();

	const covers = (override: BasePriceOverride) =>
		(override.months ?? []).includes(monthKey.value);

	const inMonth = (values: DayMap<number>): DayMap<number> =>
		Object.fromEntries(
			dates.value
				.filter((date) => values[date] !== undefined)
				.map((date) => [date, values[date]])
		);

	/** This month's days from the row that decides it — the target's own, or inherited. */
	const effectiveDays = (type: MonthDayRowType): DayMap<number> =>
		inMonth(valuesOf(store.effectiveTargetOverride(type, { covers }), type));

	/** Does this target decide this month itself? */
	const decidesOwn = (type: MonthDayRowType): boolean => {
		const own = store.targetOverride(type as BasePriceOverrideType);

		return own !== null && covers(own);
	};

	const writeOwn = (
		type: MonthDayRowType,
		values: DayMap<number>,
		months: string[]
	) => {
		store.setTargetOverride(
			type as BasePriceOverrideType,
			months.length
				? { [DAY_VALUES[type]]: values, months: [...months].sort() }
				: null
		);
	};

	/**
	 * Makes this month the target's own with exactly `values`. A month with no
	 * values is still listed when an ancestor decides it: without that, "no
	 * days set here" would quietly hand the month the ancestor's days. With
	 * nothing above to shadow it is left out, so the row stays as small as
	 * what it says.
	 */
	const writeMonth = (type: MonthDayRowType, values: DayMap<number>) => {
		const own = store.targetOverride(type as BasePriceOverrideType);
		const inherited = store.effectiveTargetOverride(type, {
			skipOwn: true,
			covers,
		});
		const decides = Object.keys(values).length > 0 || inherited !== null;

		writeOwn(
			type,
			replaceMonth(valuesOf(own, type), dates.value, decides ? values : {}),
			[
				...(own?.months ?? []).filter((m) => m !== monthKey.value),
				...(decides ? [monthKey.value] : []),
			]
		);
	};

	/** Hands this month back to whatever is above, leaving every other month as it was. */
	const clearMonth = (type: MonthDayRowType) => {
		const own = store.targetOverride(type as BasePriceOverrideType);
		if (!own) return;

		writeOwn(
			type,
			replaceMonth(valuesOf(own, type), dates.value, {}),
			(own.months ?? []).filter((m) => m !== monthKey.value)
		);
	};

	/** This target's own pinned days in this month. */
	const ownPins = (): DayMap<number> =>
		inMonth(store.targetOverride("day_budget")?.daily_amounts ?? {});

	const writePins = (values: DayMap<number>) => {
		const next = replaceMonth(
			store.targetOverride("day_budget")?.daily_amounts,
			dates.value,
			values
		);

		store.setTargetOverride(
			"day_budget",
			Object.keys(next).length ? { daily_amounts: next } : null
		);
	};

	return {
		covers,
		effectiveDays,
		decidesOwn,
		writeMonth,
		clearMonth,
		ownPins,
		writePins,
	};
};
